(() => {
  'use strict';
  const TTL = 7200000;
  const fail = code => { throw Object.assign(new Error(code), { code }); };
  const clone = value => structuredClone(value);
  const identifier = () => window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  // Approval stays in the native owner; it can never be recovered from a draft.
  const draftOnly = value => {
    const result = clone(value);
    for (const key of ['entryContinuation', 'approved', 'approval', 'previewApproved', 'previewApprovedOn']) delete result[key];
    return result;
  };
  const refs = payload => [payload?.originalImageKey, payload?.imageKey, payload?.retainedUploadKey].filter(Boolean);
  const usesPhoto = continuation => Boolean(continuation?.source
    && !(continuation.sourceIntentExplicit && ['create', 'words'].includes(continuation.sourceIntent)));
  // Shared with Studio restoration. An unfinished composition is still editable;
  // the later handoff/generation boundary requires complete illustration details.
  const validateGuidedDetails = value => {
    const failure = !value || typeof value !== 'object' || Array.isArray(value) ? 'shape'
      : Object.keys(value).sort().join(',') !== 'compositionIntent,style' ? 'keys'
      : typeof value.style !== 'string' || !value.style.trim() || value.style.length > 80 ? 'style'
      : typeof value.compositionIntent !== 'string' ? 'composition_type'
      : value.compositionIntent.length > 1000 ? 'composition_length' : '';
    if (failure) throw Object.assign(new Error('The saved illustration details need to be recovered before creating another image.'),
      { code: `GUIDED_DETAILS_${failure.toUpperCase()}` });
    return { style: value.style, compositionIntent: value.compositionIntent };
  };
  const getImageSessionId = (scene = window.DIMRuntime?.scene, storage = window.sessionStorage) => {
    if (!scene?.keys?.draft) fail('SOURCE_STORAGE_UNAVAILABLE');
    const key = `${scene.keys.draft}:image-session`;
    try {
      const existing = storage.getItem(key); if (existing) return existing;
      const created = identifier(); storage.setItem(key, created);
      if (storage.getItem(key) !== created) fail('SOURCE_STORAGE_UNAVAILABLE');
      return created;
    } catch (_) { fail('SOURCE_STORAGE_UNAVAILABLE'); }
  };
  const probeImageBlob = async blob => {
    let decoded;
    if (window.createImageBitmap) {
      try {
        decoded = await window.createImageBitmap(blob, { imageOrientation: 'from-image', premultiplyAlpha: 'default', colorSpaceConversion: 'default' });
        return { width: Number(decoded.width), height: Number(decoded.height) };
      } finally { decoded?.close?.(); }
    }
    const objectUrl = URL.createObjectURL(blob), image = new Image();
    try {
      return await new Promise((resolve, reject) => {
        image.onload = () => resolve({ width: Number(image.naturalWidth), height: Number(image.naturalHeight) });
        image.onerror = () => reject(new Error('SOURCE_DECODE_FAILED')); image.src = objectUrl;
      });
    } finally { image.onload = null; image.onerror = null; image.src = ''; URL.revokeObjectURL(objectUrl); }
  };
  // Shared by neutral Home intake and Studio. Target readiness remains Studio's.
  const validateFile = async (file, options = {}) => {
    const rule = { maximumBytes: 104857600, minimumWidth: 0, minimumHeight: 0, ...(options.rule || {}) };
    if (options.maximumBytes !== undefined) rule.maximumBytes = options.maximumBytes;
    const extension = String(file?.name || '').split('.').pop()?.toLowerCase();
    if (!['png', 'jpg', 'jpeg'].includes(extension) || !['image/png', 'image/jpeg'].includes(file?.type)) fail('SOURCE_FORMAT_INVALID');
    if (!Number.isFinite(rule.maximumBytes) || rule.maximumBytes <= 0 || file.size > rule.maximumBytes) fail('SOURCE_SIZE_INVALID');
    const signature = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const png = [137,80,78,71,13,10,26,10].every((value, index) => signature[index] === value);
    const jpeg = signature[0] === 255 && signature[1] === 216 && signature[2] === 255;
    if (!(file.type === 'image/png' && png && extension === 'png') && !(file.type === 'image/jpeg' && jpeg && ['jpg', 'jpeg'].includes(extension))) fail('SOURCE_FORMAT_INVALID');
    let inspection;
    try {
      const inspector = options.inspector || window.DreamItImageInspector;
      if (options.inspectImageBlob) inspection = await options.inspectImageBlob(file, { expectedMime: file.type, maximumBytes: rule.maximumBytes, onProgress: options.onProgress });
      else {
        if (!inspector?.inspect || !inspector?.admit) fail('SOURCE_DECODE_FAILED');
        inspection = await inspector.inspect(file, { expectedMime: file.type, maximumBytes: rule.maximumBytes, onProgress: options.onProgress });
        inspector.admit(inspection);
      }
      if (options.requireDecode !== false && (!inspection.semantic || inspection.orientation !== 1)) {
        options.onDecode?.();
        const decoded = await (options.probeImageBlob || probeImageBlob)(file);
        if (!(options.probeMatchesInspection || ((value, inspected) => value.width === inspected.width && value.height === inspected.height))(decoded, inspection)) fail('SOURCE_DECODE_FAILED');
      }
      if (!Number.isFinite(inspection.width) || !Number.isFinite(inspection.height) || inspection.width <= 0 || inspection.height <= 0) fail('SOURCE_DECODE_FAILED');
    } catch (_) { fail('SOURCE_DECODE_FAILED'); }
    if (inspection.width < rule.minimumWidth || inspection.height < rule.minimumHeight) fail('SOURCE_DIMENSIONS_INVALID');
    return { dimensions: { width: inspection.width, height: inspection.height }, inspection, rule };
  };
  function create(scene, { storage = window.sessionStorage, now = Date.now, inspector = window.DreamItImageInspector } = {}) {
    if (!scene?.readHeader || !scene?.writeHeader) fail('SOURCE_STORAGE_UNAVAILABLE');
    const read = () => {
      const current = scene.readHeader();
      const continuation = current.status === 'ready' && current.payload.entryContinuation?.version === 1 ? clone(current.payload.entryContinuation) : null;
      return { ...current, revision: current.revision ?? current.record?.headerRevision ?? 0, continuation,
        continuationStatus: !continuation ? 'empty' : continuation.expiresAt <= now() ? 'expired' : 'ready' };
    };
    const describeContinuation = () => {
      try {
        // The paid owner validates its own durable lock. Its nominal deadline
        // cannot release an accepted/uncertain request; only its status owner can.
        if (window.DIMRuntime?.scene === scene && window.DIMRuntime.paid?.pending?.()) return Object.freeze({ kind: 'studio' });
        const current = read();
        if (current.status !== 'ready') return null;
        const profiles = window.DreamItArtworkProfiles;
        const validateDraft = draft => {
          if (!draft || draft.version !== 7 || !['', 'upload', 'create'].includes(draft.route)
            || !profiles?.isUnexpired?.(draft, now())) fail('SOURCE_DRAFT_UNAVAILABLE');
          for (const field of ['productId', 'variantId', 'originalImageKey', 'retainedUploadKey', 'imageKey']) {
            if (typeof draft[field] !== 'string') fail('SOURCE_DRAFT_UNAVAILABLE');
          }
          if (draft.designOrigin === 'guided') validateGuidedDetails(draft.guidedDetails);
          if (draft.resolution) profiles.validateResolution(draft.resolution);
          if (draft.editorState) window.DIMTextModel.textState(draft.editorState);
          if (draft.composition !== undefined) {
            const composition = window.DIMSceneIdentity.validateComposition(draft.composition, { resolution: draft.resolution,
              originalImageKey: draft.originalImageKey, originalRevision: draft.originalRevision, now: now() });
            window.DIMSceneIdentity.validateCompositionText(composition, window.DIMTextModel.textState(draft.editorState));
          }
          return Boolean(draft.productId || draft.variantId || draft.originalImageKey || draft.retainedUploadKey || draft.composition
            || draft.route === 'create' && typeof draft.idea === 'string' && draft.idea.trim());
        };
        const payload = current.payload;
        if (!payload || payload.version !== 7) return null;
        if (payload.entryContinuation !== undefined) {
          const entry = current.continuation;
          if (!entry || current.continuationStatus !== 'ready' || !Number.isSafeInteger(entry.createdAt)
            || !Number.isSafeInteger(entry.expiresAt) || entry.createdAt > now() || entry.createdAt >= entry.expiresAt
            || entry.expiresAt - entry.createdAt > TTL || entry.expiresAt > current.record.expiresAt) return null;
          if (entry.intent !== undefined && !['guided', 'choose', 'upload', 'create', 'words', 'personalise'].includes(entry.intent)
            || entry.sourceIntent !== undefined && !['unknown', 'upload', 'create', 'words'].includes(entry.sourceIntent)
            || entry.rawText !== undefined && (typeof entry.rawText !== 'string' || entry.rawText.length > 10000)) return null;
          // The outer draft remains the prior owner's readable snapshot. Validate
          // its restorable fields too, without treating it as the selected source.
          validateDraft(payload);
          if (entry.candidateDraft) validateDraft(entry.candidateDraft);
          if (entry.composition !== undefined) window.DIMSceneIdentity.validateComposition(entry.composition, { now: now() });
          if (entry.source) {
            if (typeof entry.source.recordId !== 'string' || !entry.source.recordId
              || typeof entry.source.sessionId !== 'string' || !entry.source.sessionId
              || !Number.isSafeInteger(entry.source.expiresAt)) return null;
            if (usesPhoto(entry) && (entry.source.expiresAt <= now()
              || storage.getItem(`${scene.keys.draft}:image-session`) !== entry.source.sessionId)) return null;
          }
          const work = usesPhoto(entry) || entry.candidateDraft || entry.composition || entry.selection?.productId
            || entry.sourceIntent === 'create' && typeof entry.rawText === 'string' && entry.rawText.trim()
            || entry.sourceIntent === 'words' && typeof entry.wording === 'string' && entry.wording.trim();
          return work ? Object.freeze({ kind: 'studio', expiresAt: entry.expiresAt }) : null;
        }
        return validateDraft(payload) ? Object.freeze({ kind: 'studio', expiresAt: current.record.expiresAt }) : null;
      } catch (_) { return null; }
    };
    const requireWritable = current => {
      if (!['empty', 'ready', 'expired'].includes(current.status)) fail('SOURCE_DRAFT_UNAVAILABLE');
      if (current.payload?.entryContinuation && current.payload.entryContinuation.version !== 1) fail('SOURCE_CONTINUATION_UNSUPPORTED');
    };
    const persist = (payload, expiresAt, revision) => scene.writeHeader(payload, expiresAt, { expectedRevision: revision });
    const validateWords = (composition, resolution, originalImageKey = '', originalRevision = 0) => {
      if (!window.DIMSceneIdentity?.validateComposition || composition === undefined || resolution?.route !== 'upload') fail('SOURCE_WORDS_UNAVAILABLE');
      return window.DIMSceneIdentity.validateComposition(composition, { resolution, originalImageKey, originalRevision, now: now() });
    };
    const stage = (patch, { expectedRevision, expiresAt, wordsResolution, guidedSubmission = false, submitGuided = false } = {}) => {
      const current = read(); requireWritable(current);
      if (expectedRevision !== undefined && current.revision !== expectedRevision) fail('SOURCE_CONTINUATION_STALE');
      const composition = patch.composition !== undefined || patch.intent === 'words'
        ? validateWords(patch.composition, wordsResolution) : null;
      const stagedAt = now();
      const limit = (current.status === 'ready' ? current.record?.expiresAt : 0) || expiresAt || stagedAt + TTL;
      // Payload readers enforce the same fixed lifetime as image records. Anchor
      // a fresh payload to its chosen deadline, never a later clock read.
      const base = current.status === 'ready' ? draftOnly(current.payload) : { version: 7, productId: '', variantId: '', resolution: null, route: 'upload', originalImageKey: '', retainedUploadKey: '', imageKey: '', editorDirty: true, createdAt: limit - TTL, expiresAt: limit };
      const existing = current.continuation;
      const continuation = { version: 1, createdAt: existing?.createdAt || stagedAt, expiresAt: Math.min(existing?.expiresAt || limit, expiresAt || limit),
        prior: existing?.prior || (current.status === 'ready' ? { payload: base, createdAt: current.record.createdAt, expiresAt: current.record.expiresAt } : null),
        ...existing, ...clone(patch) };
      if (composition) {
        continuation.composition = clone(composition);
        continuation.expiresAt = Math.min(continuation.expiresAt, composition.expiresAt);
      }
      // A deliberate alternative starts a new candidate under the unchanged
      // outer draft lifetime. The held photo keeps its own original expiry and
      // remains recovery data; its expiry cannot become the new source's fence.
      if (patch.sourceIntentExplicit && ['create', 'words'].includes(patch.sourceIntent)) {
        continuation.expiresAt = Math.min(limit, expiresAt || limit, composition?.expiresAt || limit);
      }
      continuation.revision = current.revision + 1;
      // Home's explicit guided continue stamps one owner atomically with the
      // continuation. Ordinary saves retain that owner and original lifetime.
      if (guidedSubmission) {
        continuation.guidedSubmittedAt = stagedAt;
        continuation.guidedSubmissionRevision = continuation.revision;
      }
      if (submitGuided) {
        continuation.guidedDispatch = { version: 1, status: 'submitted', submissionRevision: continuation.revision,
          rawText: continuation.rawText, sourceRecordId: continuation.source?.recordId || '', sourceIntent: continuation.sourceIntent || 'unknown' };
      } else if (continuation.guidedDispatch?.status === 'submitted'
        && (continuation.revision !== continuation.guidedDispatch.submissionRevision || continuation.intent !== 'guided' || continuation.rawText !== continuation.guidedDispatch.rawText
          || (continuation.source?.recordId || '') !== continuation.guidedDispatch.sourceRecordId
          || (continuation.sourceIntent || 'unknown') !== continuation.guidedDispatch.sourceIntent)) {
        continuation.guidedDispatch.status = 'cancelled';
      }
      // The main payload stays readable as the unchanged prior design for old clients.
      persist({ ...base, entryContinuation: continuation }, limit, current.revision);
      return clone(continuation);
    };
    const cleanup = async (ids, keep) => {
      for (const id of new Set(ids)) {
        const live = read();
        const protectedKeys = [...keep, ...refs(live.payload), ...refs(live.continuation?.prior?.payload), live.continuation?.source?.recordId];
        if (id && !protectedKeys.includes(id)) { try { await scene.deleteImageRecord(id); } catch (_) { /* Safe orphan; normal expiry pruning owns retry. */ } }
      }
    };
    const acceptFile = async (file, options = {}) => {
      const before = read(); requireWritable(before);
      const current = () => options.isCurrent?.() !== false && read().revision === before.revision;
      if (!current()) fail('SOURCE_CONTINUATION_STALE');
      const validated = await validateFile(file, { inspector, ...options });
      if (!current()) fail('SOURCE_CONTINUATION_STALE');
      const acceptedAt = now(), expiresAt = Math.min(acceptedAt + Math.min(TTL, Math.max(1, options.ttl || TTL)), (before.status === 'ready' ? before.record?.expiresAt : 0) || Infinity);
      // A replacement inherits the protected deadline. Its lifecycle anchor must
      // still satisfy the existing strict TTL contract without renewing that date.
      const createdAt = expiresAt - TTL;
      const sessionId = getImageSessionId(scene, storage);
      const record = { id: `${sessionId}:unselected:${identifier()}`, sessionId, productId: '', resolution: null, source: 'upload', originalName: file.name,
        blob: file, mimeType: file.type, ...validated.dimensions, createdAt, expiresAt };
      let stored = false;
      try {
        await scene.putImageRecord(record);
        stored = true;
        if (!current()) fail('SOURCE_CONTINUATION_STALE');
        const verified = await scene.getImageRecord(record.id);
        if (!verified || verified.sessionId !== sessionId || verified.expiresAt <= now()) fail('SOURCE_RECORD_INVALID');
        if (!current()) fail('SOURCE_CONTINUATION_STALE');
        const continuation = stage({ sourceIntent: 'upload', sourceIntentExplicit: true, source: { recordId: record.id, sessionId, expiresAt }, expiresAt }, { expectedRevision: before.revision, expiresAt });
        // A newly accepted candidate supersedes only the former candidate asset.
        await cleanup([before.continuation?.source?.recordId], [...refs(continuation.prior?.payload), record.id]);
        return { status: 'accepted', record: verified, continuation, revision: read().revision };
      } catch (error) {
        // Cleanup only a known owned write. A stale operation never changes a header.
        if (stored && read().continuation?.source?.recordId !== record.id) await cleanup([record.id], refs(read().payload));
        throw error;
      }
    };
    const bounded = (value, limit) => { if (typeof value !== 'string' || value.length > limit) fail('SOURCE_INTENT_INVALID'); return value; };
    const metadata = value => {
      if (value === undefined) return undefined;
      if (!value || typeof value !== 'object' || Array.isArray(value)) fail('SOURCE_INTENT_INVALID');
      const serialized = JSON.stringify(value);
      if (serialized.length > 16000 || /"(?:blob|dataUrl|objectUrl|approved|approval|previewApproved|actionId|generationActionId|requestId)"\s*:/i.test(serialized)) fail('SOURCE_INTENT_INVALID');
      return JSON.parse(serialized);
    };
    const continueEntry = ({ intent, sourceIntent, sourceIntentConfirmed = false, rawText = '', wording, suggestedWording, selection, idea, eligibility, submitGuided = false, descriptionOnly } = {}) => {
      if (!['guided', 'choose', 'upload', 'create', 'words', 'personalise'].includes(intent)) fail('SOURCE_INTENT_INVALID');
      const patch = { intent, rawText: bounded(rawText, 10000) };
      // Home's description starts a fresh recommendation task. Held artwork and
      // its exact target remain recovery data, rather than an implicit attachment.
      if (intent === 'guided' && submitGuided === true && descriptionOnly === true) {
        patch.descriptionOnly = true; patch.sourceIntent = 'unknown'; patch.sourceIntentExplicit = false;
      } else if (intent !== 'guided') patch.descriptionOnly = false;
      if (sourceIntent !== undefined && !['unknown', 'upload', 'create', 'words'].includes(sourceIntent)) fail('SOURCE_INTENT_INVALID');
      if (['upload', 'create', 'words'].includes(intent)) { patch.sourceIntent = intent; patch.sourceIntentExplicit = true; }
      else if (sourceIntentConfirmed === true && sourceIntent !== undefined) {
        patch.sourceIntent = sourceIntent; patch.sourceIntentExplicit = true;
      }
      else if (sourceIntent !== undefined) {
        const existing = read().continuation;
        // A validated interpretation may fill an unknown intent; an actual file
        // or deliberate route selection retains precedence over that suggestion.
        if (!existing?.source && !existing?.sourceIntentExplicit && (!existing?.sourceIntent || existing.sourceIntent === 'unknown')) patch.sourceIntent = sourceIntent;
      }
      if (wording !== undefined) patch.wording = bounded(wording, 4000);
      if (suggestedWording !== undefined) patch.suggestedWording = bounded(suggestedWording, 4000);
      if (selection !== undefined) patch.selection = metadata(selection);
      if (idea !== undefined) patch.idea = metadata(idea);
      if (intent === 'words') {
        patch.composition = validateWords(eligibility?.composition, eligibility?.resolution);
      }
      return stage(patch, { guidedSubmission: intent === 'guided', submitGuided: intent === 'guided' && submitGuided === true,
        ...(patch.composition ? { wordsResolution: eligibility?.resolution } : {}) });
    };
    const consumeGuidedSubmission = ({ expectedRevision, submissionRevision, rawText, sourceRecordId = '' } = {}) => {
      const current = read(), entry = current.continuation, dispatch = entry?.guidedDispatch;
      if (current.continuationStatus !== 'ready' || current.revision !== expectedRevision || entry?.intent !== 'guided'
        || dispatch?.version !== 1 || dispatch.status !== 'submitted' || dispatch.submissionRevision !== submissionRevision
        || entry.revision !== submissionRevision
        || entry.rawText !== rawText || dispatch.rawText !== rawText || dispatch.sourceRecordId !== sourceRecordId
        || (entry.source?.recordId || '') !== sourceRecordId || (entry.sourceIntent || 'unknown') !== dispatch.sourceIntent) return false;
      stage({ guidedDispatch: { ...dispatch, status: 'consumed' } }, { expectedRevision });
      const retained = read().continuation?.guidedDispatch;
      return retained?.submissionRevision === submissionRevision && retained.status === 'consumed';
    };
    const selectSource = route => {
      if (!['upload', 'create'].includes(route)) fail('SOURCE_INTENT_INVALID');
      // A header choice cannot replace any unresolved operation or handoff,
      // including an unknown record whose controller must provide recovery.
      try {
        if (window.DIMRuntime?.scene === scene && window.DIMRuntime.paid?.pending?.()) return Object.freeze({ status: 'pending' });
        if (scene.keys.pending && (storage.getItem(scene.keys.pending)
          || storage.getItem(`${scene.keys.pending}:recommendation`))) return Object.freeze({ status: 'pending' });
      } catch (_) { return Object.freeze({ status: 'pending' }); }
      const current = read();
      // Plain/expired/unsupported drafts keep their established Studio entry and
      // recovery. Never start a draft or renew its deadline from navigation.
      if (current.status !== 'ready' || !current.continuation || current.continuationStatus !== 'ready'
        || !describeContinuation()) return Object.freeze({ status: 'unchanged' });
      if (current.continuation.sourceIntent === route && current.continuation.sourceIntentExplicit === true) return Object.freeze({ status: 'unchanged' });
      stage({ intent: route, sourceIntent: route, sourceIntentExplicit: true },
        { expectedRevision: current.revision, expiresAt: current.continuation.expiresAt });
      // Candidate/prior snapshots remain intact as recovery data. Studio projects
      // only the candidate matching the deliberately selected source route.
      return Object.freeze({ status: 'selected' });
    };
    const getCandidate = async () => {
      const current = read(), source = current.continuation?.source;
      if (!usesPhoto(current.continuation) || current.continuationStatus !== 'ready' || source.expiresAt <= now()) return null;
      const record = await scene.getImageRecord(source.recordId);
      if (read().revision !== current.revision) fail('SOURCE_CONTINUATION_STALE');
      if (!record || record.sessionId !== getImageSessionId(scene, storage) || record.sessionId !== source.sessionId || record.expiresAt !== source.expiresAt || record.expiresAt <= now() || record.productId !== '' || record.resolution !== null || record.source !== 'upload') return null;
      return record;
    };
    const commit = async ({ payload, expiresAt = payload?.expiresAt, expectedRevision, isCurrent = () => true, validated, loaded, persisted, adoptPrepared } = {}) => {
      const current = read(); requireWritable(current);
      if (expectedRevision !== undefined && current.revision !== expectedRevision) fail('SOURCE_CONTINUATION_STALE');
      if (!current.continuation) fail('SOURCE_CONTINUATION_MISSING');
      if (current.continuationStatus !== 'ready' || usesPhoto(current.continuation) && current.continuation.source.expiresAt <= now()) fail('SOURCE_CONTINUATION_EXPIRED');
      if (validated !== true || loaded !== true || persisted !== true || !payload?.productId || !isCurrent() || typeof adoptPrepared !== 'function') fail('SOURCE_COMMIT_NOT_READY');
      const composition = payload.composition === undefined ? null
        : validateWords(payload.composition, payload.resolution, payload.originalImageKey, payload.originalRevision);
      if (composition) {
        if (payload.originalImageKey !== '' || payload.originalRevision !== 0 || payload.route !== 'upload'
          || String(payload.variantId) !== String(payload.resolution.shopify_variant_id) || current.continuation.sourceIntent !== 'words'
          || JSON.stringify(composition) !== JSON.stringify(current.continuation.composition)
          || !window.DIMTextModel?.validateLayers || !window.DIMSceneIdentity?.validateCompositionText) fail('SOURCE_COMMIT_NOT_READY');
        const layers = window.DIMTextModel.validateLayers(payload.editorState?.textLayers, { forExport: true });
        window.DIMSceneIdentity.validateCompositionText(composition, layers, { forExport: true });
      } else if (!payload.originalImageKey) fail('SOURCE_COMMIT_NOT_READY');
      const next = draftOnly(payload);
      next.expiresAt = Math.min(expiresAt, current.continuation.expiresAt,
        usesPhoto(current.continuation) ? current.continuation.source.expiresAt : Infinity, composition?.expiresAt || Infinity);
      persist(next, next.expiresAt, current.revision);
      const committedRevision = read().revision;
      try {
        if (!isCurrent() || read().revision !== committedRevision) fail('SOURCE_CONTINUATION_STALE');
        // No await between header persistence and the editor's protected adoption.
        const adopted = adoptPrepared();
        if (adopted === false || adopted?.then) fail('SOURCE_COMMIT_ADOPTION_FAILED');
      } catch (error) {
        // A newer owner wins; otherwise restore the exact continuation and prior.
        if (read().revision === committedRevision) persist(current.payload, current.record.expiresAt, committedRevision);
        throw error;
      }
      // Cleanup cannot delay the newly committed editor or own its next action.
      void cleanup([...refs(current.continuation.prior?.payload), current.continuation.source?.recordId], refs(next)).catch(() => {});
      return { status: 'committed', payload: next, revision: read().revision };
    };
    const rollback = async ({ expectedRevision } = {}) => {
      const current = read(); requireWritable(current);
      if (expectedRevision !== undefined && current.revision !== expectedRevision) fail('SOURCE_CONTINUATION_STALE');
      if (!current.continuation) return { status: 'unchanged' };
      const prior = current.continuation.prior;
      if (prior && prior.expiresAt <= now()) fail('SOURCE_PRIOR_EXPIRED');
      const payload = draftOnly(prior?.payload || current.payload);
      persist(payload, prior?.expiresAt || current.record.expiresAt, current.revision);
      await cleanup([current.continuation.source?.recordId], refs(payload));
      return { status: 'restored', payload, revision: read().revision };
    };
    const mount = (root, { onAccepted, onError } = {}) => {
      const input = root?.matches?.('input[type="file"]') ? root : root?.querySelector?.('input[type="file"]');
      if (!input) return () => {};
      let sequence = 0;
      const change = async () => { const file = input.files?.[0]; if (!file) return; const own = ++sequence;
        try { const result = await acceptFile(file, { isCurrent: () => sequence === own }); if (sequence === own) onAccepted?.(result); }
        catch (error) { if (sequence === own) onError?.(error); }
      };
      input.addEventListener('change', change);
      return () => { sequence++; input.removeEventListener('change', change); };
    };
    return Object.freeze({ read, describeContinuation, selectSource, stage, acceptFile, continue: continueEntry, consumeGuidedSubmission, getCandidate, commit, rollback, mount });
  }
  const current = () => create(window.DIMRuntime?.scene);
  const api = { create, validateFile, validateGuidedDetails, getImageSessionId,
    describeContinuation() { try { return current().describeContinuation(); } catch (_) { return null; } } };
  for (const name of ['read', 'selectSource', 'stage', 'acceptFile', 'continue', 'consumeGuidedSubmission', 'getCandidate', 'commit', 'rollback', 'mount']) api[name] = (...args) => current()[name](...args);
  window.DIMSourceIntake = Object.freeze(api);
})();
